import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUBJECT_GROUP_ORDER } from "../lib/subjectCatalog";

export default function About() {
  const { t } = useTranslation();

  const values = [
    { icon: <NoQueueIcon />, title: t("about.value1Title"), text: t("about.value1Text"), accent: "#7269e3" },
    { icon: <DirectIcon />,  title: t("about.value2Title"), text: t("about.value2Text"), accent: "#98dea3" },
    { icon: <SpeedIcon />,   title: t("about.value3Title"), text: t("about.value3Text"), accent: "#a783a6" },
  ];

  const steps = [
    { num: "1", title: t("about.step1Title"), text: t("about.step1Text") },
    { num: "2", title: t("about.step2Title"), text: t("about.step2Text") },
    { num: "3", title: t("about.step3Title"), text: t("about.step3Text") },
  ];

  // Single source of truth: the same category catalog the search filters use
  // (lib/subjectCatalog.js + subjectGroups.* translations). The page used to
  // show a hardcoded marketing list that didn't match the real platform
  // categories.
  const fields = SUBJECT_GROUP_ORDER.map((gid) => t(`subjectGroups.${gid}`));

  return (
    <main className="min-h-screen bg-[#272c39] text-white">

      {/* ── Hero ── */}
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-5xl px-4 py-16 md:px-6 md:py-24">
          <h1 className="font-unbounded text-4xl font-extrabold leading-tight md:text-6xl">
            {t("about.heroLine1")}
            <span className="block text-[#98dea3]">{t("about.heroLine2")}</span>
          </h1>

          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/70 md:text-xl">
            {t("about.heroSubtitle")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/search"
              className="rounded-2xl bg-[#98dea3] px-6 py-3 font-semibold text-[#1f2430] transition hover:opacity-90"
            >
              {t("about.learnMoreBtn")}
            </Link>
            <Link
              to="/signup"
              className="rounded-2xl border border-white/12 bg-white/5 px-6 py-3 font-semibold text-white/90 transition hover:bg-white/10"
            >
              {t("about.joinBtn")}
            </Link>
          </div>

          {/* Value props */}
          <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {values.map((v) => (
              <div
                key={v.title}
                className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4"
              >
                <div
                  className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${v.accent}20`, color: v.accent, border: `1px solid ${v.accent}30` }}
                >
                  {v.icon}
                </div>
                <div>
                  <div className="font-semibold text-white">{v.title}</div>
                  <div className="mt-0.5 text-sm text-white/55">{v.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 md:px-6">

        {/* ── For clients / For specialists ── */}
        <section className="py-14 md:py-20">
          <div className="grid gap-4 md:grid-cols-2">

            {/* Clients */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#7269e3]/20 text-[#a89ef5] border border-[#7269e3]/25">
                <SearchIcon />
              </div>
              <h2 className="text-xl font-bold">{t("about.clientTitle")}</h2>
              <p className="mt-3 leading-relaxed text-white/65">
                {t("about.clientText1")}
              </p>
              <p className="mt-3 leading-relaxed text-white/65">
                {t("about.clientText2")}
              </p>
              <Link
                to="/search"
                className="mt-5 inline-block rounded-xl bg-[#7269e3]/15 px-4 py-2 text-sm font-semibold text-[#a89ef5] ring-1 ring-[#7269e3]/25 hover:bg-[#7269e3]/25 transition"
              >
                {t("about.clientCta")}
              </Link>
            </div>

            {/* Specialists */}
            <div className="rounded-2xl border border-[#98dea3]/20 bg-[#98dea3]/[0.06] p-7">
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#98dea3]/15 text-[#98dea3] border border-[#98dea3]/25">
                <StarIcon />
              </div>
              <h2 className="text-xl font-bold">{t("about.specialistTitle")}</h2>
              <p className="mt-3 leading-relaxed text-white/65">
                {t("about.specialistText1")}
              </p>
              <p className="mt-3 leading-relaxed text-white/65">
                {t("about.specialistText2")}
              </p>
              <Link
                to="/signup"
                className="mt-5 inline-block rounded-xl bg-[#98dea3]/15 px-4 py-2 text-sm font-semibold text-[#98dea3] ring-1 ring-[#98dea3]/25 hover:bg-[#98dea3]/25 transition"
              >
                {t("about.specialistCta")}
              </Link>
            </div>
          </div>
        </section>

        {/* ── Fields ── */}
        <section className="pb-14 md:pb-20">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 md:p-10">
            <h2 className="text-2xl font-bold md:text-3xl">{t("about.fieldsTitle", { count: fields.length })}</h2>
            <p className="mt-2 text-white/55">{t("about.fieldsSub")}</p>
            {/* Responsive grid instead of free-flowing chips: long category
                names (e.g. "Музичний та звуковий продюсер") wrap inside their
                cell, so nothing overflows or gets cut off on phones. */}
            <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {fields.map((f) => (
                <span
                  key={f}
                  className="flex min-h-[3rem] items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] px-3 py-2 text-center text-sm leading-snug text-white/75"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works ── */}
        <section className="pb-14 md:pb-20">
          <h2 className="text-2xl font-bold md:text-3xl">{t("about.howTitle")}</h2>
          <p className="mt-2 text-white/55">{t("about.howSub")}</p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {steps.map((s) => (
              <div
                key={s.num}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#7269e3]/20 text-sm font-bold text-[#a89ef5]">
                  {s.num}
                </span>
                <h3 className="mt-4 font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="pb-16 md:pb-20">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-[#98dea3]/20 bg-[#98dea3]/[0.08] p-8">
              <p className="text-sm text-[#98dea3] font-medium uppercase tracking-widest">{t("about.ctaForClients")}</p>
              <h3 className="mt-3 text-2xl font-bold">{t("about.ctaClientTitle")}</h3>
              <p className="mt-3 leading-relaxed text-white/65">{t("about.ctaClientText")}</p>
              <Link
                to="/search"
                className="mt-5 inline-block rounded-2xl bg-[#98dea3] px-6 py-3 font-semibold text-[#1f2430] transition hover:opacity-90"
              >
                {t("about.ctaFind")}
              </Link>
            </div>
            <div className="rounded-2xl border border-[#7269e3]/20 bg-[#7269e3]/[0.08] p-8">
              <p className="text-sm text-[#a89ef5] font-medium uppercase tracking-widest">{t("about.ctaForSpecialists")}</p>
              <h3 className="mt-3 text-2xl font-bold">{t("about.ctaSpecialistTitle")}</h3>
              <p className="mt-3 leading-relaxed text-white/65">{t("about.ctaSpecialistText")}</p>
              <Link
                to="/signup"
                className="mt-5 inline-block rounded-2xl bg-[#7269e3] px-6 py-3 font-semibold text-white transition hover:opacity-90"
              >
                {t("about.ctaJoin")}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3-3" strokeLinecap="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M12 3l2.2 4.8L19 10l-4.8 2.2L12 17l-2.2-4.8L5 10l4.8-2.2L12 3z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpeedIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NoQueueIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <rect x="3" y="4" width="18" height="3" rx="1.5" />
      <rect x="3" y="10.5" width="12" height="3" rx="1.5" />
      <path d="M16 14l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DirectIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.9">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
